/**
 * O editor, e o que o canal deixa fazer (H05, §8.2).
 *
 * ── A regra que organiza este arquivo ─────────────────────────────────────
 *
 * *"A interface consulta `capabilities` antes de habilitar a ação."* Habilitar
 * e deixar o servidor recusar frustra de um jeito específico: o vendedor
 * escreve a resposta inteira, tenta enviar, e perde o texto.
 *
 * Então tudo aqui acontece ANTES do clique — a contagem da janela, o seletor de
 * template, o contador de caracteres, o motivo da recusa. Depois do clique só
 * sobra o que depende do servidor.
 *
 * ── E a que organiza cada recusa ──────────────────────────────────────────
 *
 * Ação indisponível **sempre com explicação**. "Fora da janela de 24 h — use um
 * template aprovado" ensina; botão cinza sem motivo gera chamado no suporte.
 * Por isso o motivo aparece acima do campo, e não num `title` que só quem passa
 * o mouse encontra.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  aceitaAnexo,
  escolher,
  esquecer,
  recusaDe,
  tamanhoEmPalavras,
  tiposParaOSeletor,
  type AnexoEscolhido,
} from '../lib/anexos';
import {
  duracaoEmPalavras,
  gravar,
  melhorFormato,
  podeGravar,
  type GravacaoEmCurso,
} from '../lib/gravador';
import { DialogoDeModelos, type SituacaoDasProntas } from './dialogo-de-modelos';
import { DialogoDeCompartilharContato } from './dialogo-de-compartilhar-contato';
import { filtrarPorTitulo, lerComando, proximoDestaque } from '../lib/comando-de-mensagem-pronta';
import { useMencionaveis, useMensagensProntas } from '@/shared/api/consultas';
import { filtrarPessoas, inserirMencao, lerMencao, mencoesNoTexto } from '../lib/mencao-no-editor';
import { dividirEnvio } from '../lib/envio';
import { inserirNoCursor } from '../lib/emojis';
import { juntarNaMensagem } from '../lib/precos';
import { SeletorDeEmoji } from './seletor-de-emoji';
import { IconeAnexo } from '@/shared/ui/icones-da-conversa';
import { Editor, type Janela } from '@/shared/ui';
import { usePode } from '@/shared/sessao/contexto';
import type { RascunhoDeMensagemPronta } from '@/entities/atendimento/mensagem-pronta';
import {
  JANELA_APERTADA_MS,
  medirConteudo,
  podeEnviarAgora,
  tempoRestanteEmPalavras,
  type Capacidades,
  type EstadoDaJanela,
  type Excesso,
} from '../lib/capacidades';

export function EditorDaConversa({
  conversaId,
  aoEnviar,
  canal,
  capacidades,
  janela,
  paraInserir = null,
  aoInserir,
}: {
  /** A conversa, para a lista do "@" da nota interna (#569). */
  conversaId: string;
  aoEnviar: (
    texto: string,
    privada: boolean,
    templateNome?: string,
    anexos?: readonly File[],
    /** Um valor por variável do modelo, na ordem. */
    templateValores?: readonly string[],
    /** Quem a nota interna chama com "@" (#569). */
    mencoes?: readonly string[],
  ) => void;
  canal: string | null;
  capacidades: Capacidades;
  janela: EstadoDaJanela;
  /**
   * Um texto de fora para a caixa (o "Copiar" dos preços, #1085). Entra no fim
   * do que já estava escrito, sem enviar; o editor avisa por `aoInserir` que o
   * consumiu.
   */
  paraInserir?: string | null;
  aoInserir?: () => void;
}): ReactNode {
  const [texto, definirTexto] = useState('');
  const [privada, definirPrivada] = useState(false);
  const [template, definirTemplate] = useState('');
  /** Um valor por variável do modelo, na ordem: o primeiro é `{{1}}`. */
  const [valores, definirValores] = useState<string[]>([]);
  const [anexos, definirAnexos] = useState<readonly AnexoEscolhido[]>([]);
  const [modelosAbertos, definirModelosAbertos] = useState(false);
  const [contatoAberto, definirContatoAberto] = useState(false);
  /*
   * A lista da organização, para a caixa de mensagens prontas.
   *
   * Buscada aqui, e não dentro do diálogo: o cache é longo (a lista muda
   * algumas vezes por mês), então ela já está quente quando alguém abre a
   * caixa — e abrir a caixa é o momento em que se está com o cliente esperando.
   */
  const mensagensProntas = useMensagensProntas();
  /** Quem cria mensagem pronta pela própria caixa (#1192). */
  const podeCriarPronta = usePode('mensagem_pronta', 'editar');
  /*
   * A mensagem pronta que está sendo escrita na caixa, ou `null`.
   *
   * Mora aqui, e não no diálogo, porque o diálogo some com Esc ou com um
   * clique fora, e com ele iria o texto meio escrito.
   */
  const [rascunhoDaPronta, definirRascunhoDaPronta] = useState<RascunhoDeMensagemPronta | null>(
    null,
  );
  const situacaoDasProntas: SituacaoDasProntas = mensagensProntas.data
    ? 'pronta'
    : mensagensProntas.isError
      ? 'erro'
      : 'carregando';

  /*
   * O comando de barra (H05c).
   *
   * `destaque` é qual item o Enter escolhe; `comandoFechado` guarda o Esc —
   * sem ele, fechar a lista com o texto ainda começando por `/` a reabriria no
   * próximo caractere digitado, e a pessoa não teria como continuar escrevendo
   * uma frase que comece com barra.
   */
  const [destaque, definirDestaque] = useState(0);
  const [comandoFechado, definirComandoFechado] = useState(false);

  /*
   * O "@" da nota interna (#569).
   *
   * `escolhidas` guarda quem foi escolhido na lista (id → nome): o texto só
   * tem o nome, e o envio precisa do id. `mencaoFechada` é o Esc, como o do
   * "/", e vale para aquele "@" — o próximo reabre.
   */
  const [escolhidas, definirEscolhidas] = useState<ReadonlyMap<string, string>>(new Map());
  const [destaqueDaMencao, definirDestaqueDaMencao] = useState(0);
  const [mencaoFechadaEm, definirMencaoFechadaEm] = useState<number | null>(null);
  const [avisoDaMencao, definirAvisoDaMencao] = useState<string | null>(null);
  const seletor = useRef<HTMLInputElement>(null);

  /*
   * As prévias devolvem a memória quando o editor sai da tela.
   *
   * `createObjectURL` reserva o blob até alguém revogar, e uma tela de
   * atendimento fica aberta o dia inteiro trocando de conversa. Sem isto, cada
   * imagem escolhida e abandonada fica retida até a aba fechar.
   */
  const [emojisAbertos, definirEmojisAbertos] = useState(false);
  const campo = useRef<HTMLTextAreaElement>(null);

  /*
   * O "Copiar" dos preços (#1085): o texto entra no fim da caixa e o foco vem
   * para cá, sem enviar. A ref guarda o que já entrou: no celular o editor
   * monta com o texto pendente, e o efeito que roda duas vezes no modo estrito
   * não pode colá-lo duas vezes.
   */
  const inserido = useRef<string | null>(null);
  useEffect(() => {
    if (paraInserir === null) {
      inserido.current = null;
      return;
    }
    if (inserido.current === paraInserir) return;
    inserido.current = paraInserir;
    definirTexto((atual) => juntarNaMensagem(atual, paraInserir));
    campo.current?.focus();
    aoInserir?.();
  }, [paraInserir, aoInserir]);
  const [gravacao, definirGravacao] = useState<GravacaoEmCurso | null>(null);
  const [segundos, definirSegundos] = useState(0);
  const [falhaDoMicrofone, definirFalhaDoMicrofone] = useState<string | null>(null);

  /*
   * O relógio da gravação.
   *
   * Existe para a pessoa saber que está gravando — sem ele, um botão que mudou
   * de cor é a única pista, e um áudio de três minutos gravado por engano é
   * descoberto só no envio.
   */
  useEffect(() => {
    if (!gravacao) return undefined;
    const relogio = setInterval(() => definirSegundos((s) => s + 1), 1000);
    return () => clearInterval(relogio);
  }, [gravacao]);

  const gravacaoAtual = useRef<GravacaoEmCurso | null>(null);
  gravacaoAtual.current = gravacao;

  /*
   * Sair da tela no meio da gravação DEVOLVE o microfone.
   *
   * Sem isto, trocar de conversa enquanto grava deixa a luz do microfone acesa
   * — o navegador continua dizendo que o site está ouvindo, depois de a tela
   * que ouvia ter sumido.
   */
  useEffect(() => {
    return () => gravacaoAtual.current?.cancelar();
  }, []);

  const anexosAtuais = useRef<readonly AnexoEscolhido[]>([]);
  anexosAtuais.current = anexos;

  useEffect(() => {
    /*
     * Pela ref, e não pela variável.
     *
     * Um efeito com dependências vazias fecha sobre o valor do PRIMEIRO
     * render — a lista vazia — e a limpeza não revogaria nada. Pôr `anexos`
     * nas dependências seria pior: revogaria a prévia a cada mudança da
     * lista, e a imagem sumiria da tela enquanto ainda está anexada.
     *
     * A ref é sempre o valor atual, e o efeito roda uma vez só.
     */
    return () => {
      for (const anexo of anexosAtuais.current) esquecer(anexo);
    };
  }, []);

  /**
   * A nota interna não passa por regra de canal nenhuma.
   *
   * Ela não sai pelo canal, então janela, template e limite do WhatsApp não a
   * alcançam. Bloquear a nota porque a janela fechou impediria justamente o
   * registro que o vendedor precisa deixar quando NÃO consegue responder.
   */
  const permissao = privada
    ? { podeEnviar: true, porque: null, exigeTemplate: false, ressalva: null }
    : podeEnviarAgora(capacidades, janela, template !== '');

  const medida = privada ? null : medirConteudo(texto, capacidades);
  const passouDoLimite = medida?.excesso === 'passou-do-limite';

  const recusaDeAnexo = recusaDe(anexos, capacidades.limitesDeAnexo, capacidades.canal, privada);
  const podeAnexar = aceitaAnexo(capacidades.limitesDeAnexo);

  /*
   * Com anexo, texto vazio deixa de bloquear.
   *
   * Mandar só uma foto é uma mensagem completa em qualquer aplicativo de
   * conversa, e exigir uma legenda faria o vendedor digitar um ponto para
   * conseguir enviar.
   */
  /*
   * Travado ate escolher a mensagem pronta.
   *
   * `exigeTemplate` ja e a resposta do calculo de permissao para "o prazo
   * fechou (ou nunca abriu) e este canal so aceita modelo". A nota interna
   * escapa por definicao: ela nao sai pelo canal, e travar o registro que o
   * vendedor precisa deixar JUSTAMENTE quando nao consegue responder seria o
   * oposto do que a trava existe para fazer.
   */
  /** O modelo escolhido, com corpo e contagem de variáveis. */
  const escolhido =
    template === ''
      ? undefined
      : capacidades.template.catalogo.find((item) => item.nome === template);

  /*
   * O texto com os valores dentro — é ele que o cliente vai ler.
   *
   * Mostrado enquanto se digita: a pessoa vê a frase montando, e não um nome
   * de modelo em `snake_case` que não diz nada a ninguém.
   */
  const preenchido = escolhido
    ? escolhido.corpo.replace(/\{\{\s*(\d+)\s*\}\}/g, (inteiro, numero: string) => {
        const valor = valores[Number(numero) - 1];
        return valor && valor.trim() !== '' ? valor : inteiro;
      })
    : '';

  /*
   * Faltando valor, o envio espera. A Meta recusaria por contagem, e o erro
   * dela fala em número — quem atende leria "parâmetros não conferem" sem
   * saber o que preencher.
   */
  const faltaValor =
    escolhido !== undefined &&
    Array.from({ length: escolhido.variaveis }).some((_, i) => (valores[i] ?? '').trim() === '');

  const travadoAteEscolherModelo = !privada && permissao.exigeTemplate && template === '';

  /*
   * Com MODELO escolhido, campo vazio não é mensagem vazia.
   *
   * O texto vem do modelo aprovado — a Meta o monta, e o que se digita no
   * campo nunca sai. Exigir texto aqui obrigava a escrever qualquer coisa só
   * para destravar o botão, e aquilo ia para o histórico como se tivesse sido
   * enviado. Foi o que travou o envio em 13/09/2026.
   */
  const vazio = texto.trim() === '' && anexos.length === 0 && template === '';

  const prontasDaOrganizacao = mensagensProntas.data?.itens ?? [];
  const comando = lerComando(texto);
  /*
   * Pelo TÍTULO, e não pelo texto.
   *
   * A caixa busca nos dois — ali quem procura lembra do conteúdo. Aqui a
   * pessoa está dizendo o NOME do que quer: casar o conteúdo fazia `/ajuda`
   * trazer junto uma mensagem chamada "Questionamento", só porque o texto dela
   * termina em "…te ajudar com mais facilidade?".
   */
  const sugeridas = comando.ativo ? filtrarPorTitulo(prontasDaOrganizacao, comando.termo) : [];

  /*
   * A lista abre com a barra, e abre MESMO sem nada para listar.
   *
   * A primeira versão fechava quando o termo não casava, e estava errada: quem
   * digita `/zzz` fica sem saber se o comando não existe, se a mensagem não
   * existe, ou se o CliqCRM travou. A caixa dizendo "nenhuma com zzz" responde
   * as três — e é o mesmo motivo pelo qual, numa organização sem nenhuma
   * cadastrada, ela abre dizendo onde se cadastra.
   *
   * Quem quiser escrever uma frase que começa com barra fecha com Esc, e a
   * lista não volta enquanto o texto não deixar de ser comando.
   *
   * Fora da janela de 24 h ela nem aparece: o campo está travado e o texto
   * livre não seria entregue — oferecer um atalho para o que não sai seria
   * ensinar um caminho que termina em recusa.
   */
  const listaAberta = !privada && !travadoAteEscolherModelo && comando.ativo && !comandoFechado;

  /*
   * O "@" só existe na nota interna: na mensagem ao cliente ele é só um
   * caractere, para nenhum nome de colega sair pelo canal por engano.
   *
   * O cursor é lido do campo a cada desenho — digitar muda o texto, e o texto
   * novo desenha de novo com o cursor já no lugar.
   */
  const cursor = campo.current?.selectionStart ?? texto.length;
  const mencao = privada ? lerMencao(texto, cursor) : { ativo: false, termo: '', inicio: -1 };
  const mencaoEmAberto = mencao.ativo && mencaoFechadaEm !== mencao.inicio;
  const mencionaveis = useMencionaveis(conversaId, mencaoEmAberto);
  const pessoasDaMencao = filtrarPessoas(mencionaveis.data?.itens ?? [], mencao.termo);
  /*
   * O termo aceita espaço, porque nome tem sobrenome. Mas termo com espaço que
   * não casa com ninguém já é frase ("@Ana e o João vão ver"), não busca: a
   * lista some em vez de insistir em "Ninguém com...". Sem espaço, o aviso de
   * que ninguém casa continua, que é a resposta útil para um nome errado.
   */
  const viraramFrase =
    mencionaveis.data !== undefined &&
    pessoasDaMencao.length === 0 &&
    /\s/.test(mencao.termo.trim());
  const listaDaMencaoAberta = mencaoEmAberto && !viraramFrase;

  /*
   * O "@" que já foi resolvido (escolhido ou fechado com Esc) fica fechado até
   * deixar de existir. Quando a pessoa apaga o "@", a trava se solta: um "@"
   * novo, mesmo na mesma posição, abre a lista de novo.
   */
  useEffect(() => {
    if (!mencao.ativo) definirMencaoFechadaEm(null);
  }, [mencao.ativo]);

  useEffect(() => {
    definirDestaqueDaMencao(0);
  }, [mencao.termo, pessoasDaMencao.length]);

  function escolherMencao(indice: number): void {
    const pessoa = pessoasDaMencao[indice];
    if (!pessoa) return;
    if (!pessoa.pode_ver) {
      // Avisa ANTES de enviar, em vez de mandar um link que ela não abre.
      definirAvisoDaMencao(`${pessoa.nome} não pode ser avisada: ${pessoa.motivo ?? 'sem acesso'}`);
      return;
    }
    const inserido = inserirMencao(texto, mencao, cursor, pessoa.nome);
    definirTexto(inserido.texto);
    definirEscolhidas((atuais) => new Map(atuais).set(pessoa.usuario_id, pessoa.nome));
    // Escolhida a pessoa, este "@" está resolvido: o que vem depois do nome é
    // o resto da frase, não mais busca.
    definirMencaoFechadaEm(mencao.inicio);
    definirAvisoDaMencao(null);
    requestAnimationFrame(() => {
      const elemento = campo.current;
      elemento?.focus();
      elemento?.setSelectionRange(inserido.cursor, inserido.cursor);
    });
  }

  /* O destaque volta ao topo quando a lista muda: o item 3 de uma lista de
     cinco não é o item 3 da lista de dois que sobrou ao digitar mais uma letra. */
  useEffect(() => {
    definirDestaque(0);
  }, [comando.termo, sugeridas.length]);

  /* Deixou de ser comando (apagou a barra, ou enviou): o Esc é esquecido. */
  useEffect(() => {
    if (!comando.ativo) definirComandoFechado(false);
  }, [comando.ativo]);

  /**
   * Escolher SUBSTITUI o campo — ver o cabeçalho do módulo do comando.
   *
   * O campo inteiro era o comando: inserir no cursor deixaria `/ajuda` grudado
   * no começo da resposta, e alguém acabaria mandando isso ao cliente.
   */
  function escolherSugerida(indice: number): void {
    const escolhida = sugeridas[indice];
    if (!escolhida) return;

    definirTexto(escolhida.texto);
    definirComandoFechado(false);
    requestAnimationFrame(() => {
      const elemento = campo.current;
      elemento?.focus();
      elemento?.setSelectionRange(escolhida.texto.length, escolhida.texto.length);
    });
  }
  const bloqueado = !permissao.podeEnviar || passouDoLimite || recusaDeAnexo !== null || faltaValor;

  function acrescentar(novos: readonly File[]): void {
    if (novos.length === 0) return;
    definirAnexos((atuais) => [...atuais, ...novos.map(escolher)]);
  }

  async function comecarAGravar(): Promise<void> {
    definirFalhaDoMicrofone(null);
    definirSegundos(0);
    try {
      definirGravacao(await gravar(melhorFormato(capacidades.limitesDeAnexo.mimeAceitos)));
    } catch (erro: unknown) {
      // Negar o microfone não é falha do sistema, e a saída é um ajuste do
      // navegador que só a pessoa pode fazer. A mensagem diz isso.
      definirFalhaDoMicrofone(
        erro instanceof Error ? erro.message : 'Não foi possível acessar o microfone.',
      );
    }
  }

  async function encerrarGravacao(): Promise<void> {
    const atual = gravacao;
    if (!atual) return;
    definirGravacao(null);
    acrescentar([await atual.parar()]);
  }

  function descartarGravacao(): void {
    gravacao?.cancelar();
    definirGravacao(null);
    definirSegundos(0);
  }

  /**
   * Põe um trecho onde o cursor está e devolve o foco.
   *
   * Serve ao emoji e à mensagem pronta da organização, e por uma razão comum:
   * os dois entram NO CURSOR, sem apagar o que já foi escrito. Quem digitou
   * "bom dia, " e escolheu o texto do horário quer os dois — substituir jogaria
   * fora o que a pessoa acabou de escrever, sem aviso e sem volta.
   *
   * Devolver o foco é o que permite escolher dois emojis seguidos e continuar
   * digitando sem tocar no mouse. Sem isso, cada escolha obriga a clicar no
   * campo de novo — e o cursor volta para o fim do texto.
   */
  function inserirNoCampo(trecho: string): void {
    const elemento = campo.current;
    const inicio = elemento?.selectionStart ?? texto.length;
    const fim = elemento?.selectionEnd ?? texto.length;
    const resultado = inserirNoCursor(texto, inicio, fim, trecho);

    definirTexto(resultado.texto);
    requestAnimationFrame(() => {
      elemento?.focus();
      elemento?.setSelectionRange(resultado.cursor, resultado.cursor);
    });
  }

  function remover(id: string): void {
    definirAnexos((atuais) => {
      const alvo = atuais.find((anexo) => anexo.id === id);
      if (alvo) esquecer(alvo);
      return atuais.filter((anexo) => anexo.id !== id);
    });
  }

  function enviar(): void {
    if (vazio || bloqueado) return;

    /**
     * O campo esvazia agora, e isso NÃO é o otimismo que a §11.1 proíbe.
     *
     * O que ela proíbe é mostrar como ENVIADA uma mensagem que a API ainda pode
     * recusar. O texto vai para a fila de envio e aparece no histórico como
     * `pendente` — esmaecido, sem hora, dizendo "enviando".
     *
     * E é a fila, não o campo, que guarda o texto se a recusa vier. Segurar o
     * campo até a resposta travaria a digitação da próxima mensagem e perderia
     * o texto se o vendedor resolvesse escrever outra coisa.
     */
    /*
     * Várias fotos viram várias MENSAGENS — ver `dividirEnvio`.
     *
     * O provedor do WhatsApp dentro do motor manda só o primeiro anexo de cada
     * mensagem (`attachments.first`). Sem a divisão, a tela mostrava três, o
     * vendedor via três, e o cliente recebia uma — sem erro em lugar nenhum.
     */
    /*
     * Com modelo, o texto do envio é a PRÉVIA preenchida — a frase que o
     * cliente vai receber —, e não o que está no campo.
     *
     * O campo fica vazio quando se escolhe um modelo, e o balão de "enviando"
     * nascia com esse vazio. A mensagem de verdade chegava com o texto do
     * modelo, os dois não casavam no pareamento (`jaProjetado`, por
     * conteúdo), e o balão vazio ficava para sempre embaixo da mensagem.
     *
     * A API não muda de comportamento: com modelo ela ignora este texto e
     * monta o conteúdo pelo catálogo (`montarConteudoDoTemplate`), com a mesma
     * substituição de `{{n}}` que monta `preenchido` aqui.
     */
    const textoDoEnvio = escolhido ? preenchido : texto.trim();

    for (const parte of dividirEnvio(
      textoDoEnvio,
      privada,
      template || undefined,
      anexos.map(({ arquivo }) => arquivo),
    )) {
      aoEnviar(
        parte.texto,
        privada,
        parte.templateNome,
        parte.arquivos,
        // Os valores só acompanham o modelo — sem ele não há o que preencher.
        parte.templateNome ? valores.slice(0, escolhido?.variaveis ?? 0) : undefined,
        // Só quem ainda está citado no texto (#569).
        privada ? mencoesNoTexto(parte.texto, escolhidas) : [],
      );
    }
    definirTexto('');
    definirEscolhidas(new Map());
    definirAvisoDaMencao(null);
    definirTemplate('');
    definirValores([]);
    /*
     * A lista esvazia, mas as prévias NÃO são revogadas aqui.
     *
     * Os arquivos foram para a fila de envio, e se a API recusar o botão de
     * repetir reenvia os mesmos — a H16 exige que a falha preserve o arquivo.
     * Revogar a prévia agora quebraria a miniatura de uma mensagem que ainda
     * está viva na tela.
     */
    definirAnexos([]);
  }

  /*
   * A janela, na língua da casca.
   *
   * Nota interna não tem janela. Fora do prazo com mensagem pronta exigida, a
   * casca trava o campo e os ícones e oferece a saída — o motivo vem do canal,
   * e o rótulo da ação é o nome que o produto dá aos modelos. Fora do prazo com
   * modelo já escolhido, a linha "Mensagem pronta: …" logo acima diz tudo, e
   * a casca fica livre. Faltando menos de uma hora, o aviso mostarda.
   */
  const horas = capacidades.janela.duracaoHoras ?? 24;
  const restante = tempoRestanteEmPalavras(janela.restanteMs);
  const janelaDaCasca: Janela = privada
    ? { estado: 'aberta' }
    : travadoAteEscolherModelo
      ? {
          estado: 'fechada',
          texto: permissao.porque ?? undefined,
          aoUsarModelo: () => definirModelosAbertos(true),
          rotuloDaAcao: 'Escolher mensagem pronta',
        }
      : !janela.existe || janela.nuncaAbriu
        ? { estado: 'aberta' }
        : !janela.aberta
          ? template !== ''
            ? { estado: 'aberta' }
            : {
                estado: 'fechada',
                texto:
                  permissao.porque ??
                  `Janela de ${horas} h fechada${capacidades.janela.reabreCom ? ` — reabre com ${capacidades.janela.reabreCom}` : ''}.`,
              }
          : restante && (janela.restanteMs ?? 0) < JANELA_APERTADA_MS
            ? { estado: 'acabando', emPalavras: restante }
            : { estado: 'aberta' };

  const motivoParaNaoEnviar = bloqueado
    ? passouDoLimite
      ? `Passou do limite de ${medida?.limite} ${capacidades.unidadeDoLimite}`
      : // Faltando valor, o motivo é ESPECÍFICO: botão apagado sem explicação
        // vira chamado no suporte, e aqui a pessoa tem o que fazer.
        faltaValor
        ? 'Preencha os valores da mensagem pronta.'
        : 'O envio está bloqueado. O motivo está acima do campo.'
    : undefined;

  return (
    <Editor
      seletorDeEmoji={
        emojisAbertos ? (
          <SeletorDeEmoji
            aoEscolher={inserirNoCampo}
            aoFechar={() => definirEmojisAbertos(false)}
          />
        ) : null
      }
      valor={texto}
      aoMudar={definirTexto}
      aoEnviar={enviar}
      modo={privada ? 'nota' : 'canal'}
      aoTrocarModo={(modo) => definirPrivada(modo === 'nota')}
      nomeDoCanal={canal ?? 'Responder'}
      janela={janelaDaCasca}
      aoAnexar={
        podeAnexar
          ? {
              aoClicar: () => seletor.current?.click(),
              rotulo: 'Anexar arquivo ou foto',
              motivo: gravacao ? 'Conclua a gravação primeiro.' : undefined,
            }
          : undefined
      }
      aoGravar={
        podeAnexar && podeGravar()
          ? {
              aoClicar: () => (gravacao ? void encerrarGravacao() : void comecarAGravar()),
              rotulo: gravacao ? 'Concluir gravação' : 'Gravar áudio',
              pressionado: gravacao !== null,
            }
          : undefined
      }
      aoEmoji={{
        aoClicar: () => definirEmojisAbertos((aberto) => !aberto),
        expandido: emojisAbertos,
      }}
      /*
       * As mensagens prontas, a QUALQUER momento.
       *
       * Antes elas só existiam dentro do aviso de janela fechada — quem quisesse
       * mandar "seu carro está pronto para retirada" no meio de uma conversa
       * viva tinha de digitar a frase à mão, e aí ela sai como texto livre: não
       * é a frase que a Meta aprovou, e não conta como modelo em lugar nenhum.
       *
       * Em nota interna o ícone não aparece: a nota fica na equipe, e mensagem
       * pronta é coisa que vai para o cliente.
       *
       * Sem nada para mostrar ele aparece DESABILITADO, com o porquê. Sumir faria
       * quem procura concluir que o produto não tem o recurso.
       *
       * "Nada" são as DUAS abas vazias, e quem pode criar nunca está nesse
       * caso: a caixa é onde ele cadastra a primeira (#1192). Antes, só o
       * catálogo da Meta contava, e a conexão sem modelo aprovado (Instagram,
       * ou um número novo) fechava junto a aba da organização.
       *
       * E vazia é a lista que CHEGOU vazia. Carregando ou com falha, a caixa
       * abre e diz isso, em vez de afirmar que a organização não tem nenhuma.
       */
      aoMensagemPronta={
        privada
          ? undefined
          : {
              aoClicar: () => definirModelosAbertos(true),
              rotulo: 'Mensagens prontas',
              expandido: modelosAbertos,
              motivo:
                capacidades.template.catalogo.length === 0 &&
                situacaoDasProntas === 'pronta' &&
                prontasDaOrganizacao.length === 0 &&
                !podeCriarPronta
                  ? 'Nenhuma mensagem pronta nem modelo aprovado nesta conexão ainda.'
                  : gravacao
                    ? 'Conclua a gravação primeiro.'
                    : undefined,
            }
      }
      /*
       * Compartilhar contato (#595). Não aparece em nota interna: o contato é
       * para o cliente. E, por ora, só no WhatsApp: o texto usa o negrito de
       * asterisco e o link wa.me, que só lá fazem sentido. Fora da janela, a
       * casca o trava com o mesmo motivo do campo.
       */
      aoCompartilharContato={
        privada || capacidades.canal !== 'whatsapp_cloud'
          ? undefined
          : {
              aoClicar: () => definirContatoAberto(true),
              rotulo: 'Compartilhar contato',
              expandido: contatoAberto,
              motivo:
                template !== ''
                  ? 'Com mensagem pronta escolhida, o texto sai dela.'
                  : gravacao
                    ? 'Conclua a gravação primeiro.'
                    : undefined,
            }
      }
      motivoParaNaoEnviar={motivoParaNaoEnviar}
      invalido={passouDoLimite}
      temAnexo={anexos.length > 0}
      // Com modelo escolhido, o texto sai dele: o campo trava e o Enviar
      // libera sem precisar de nada digitado.
      temMensagemPronta={!privada && template !== ''}
      aoSoltarArquivos={podeAnexar ? acrescentar : undefined}
      campoRef={campo}
      sugestoes={
        listaDaMencaoAberta
          ? {
              aberta: true,
              itens: pessoasDaMencao.map((pessoa) => ({
                id: pessoa.usuario_id,
                titulo: pessoa.pode_ver ? pessoa.nome : `${pessoa.nome} · ${pessoa.motivo ?? ''}`,
                texto: '',
              })),
              ativo: destaqueDaMencao,
              vazio: mencionaveis.isPending
                ? 'Buscando quem pode ser mencionado…'
                : `Ninguém com "${mencao.termo}" nesta organização.`,
              aoMover: (delta) =>
                definirDestaqueDaMencao((atual) =>
                  proximoDestaque(atual, delta, pessoasDaMencao.length),
                ),
              aoEscolher: escolherMencao,
              aoFechar: () => definirMencaoFechadaEm(mencao.inicio),
            }
          : {
              aberta: listaAberta,
              itens: sugeridas.map((m) => ({ id: m.id, titulo: m.titulo, texto: m.texto })),
              ativo: destaque,
              /*
               * Carregando ou com falha não é "nenhuma cadastrada": a lista do
               * comando diz a mesma coisa que a caixa (#1192).
               */
              vazio:
                situacaoDasProntas === 'carregando'
                  ? 'Buscando as mensagens prontas…'
                  : situacaoDasProntas === 'erro'
                    ? 'Não foi possível buscar as mensagens prontas.'
                    : prontasDaOrganizacao.length === 0
                      ? podeCriarPronta
                        ? 'Nenhuma mensagem pronta cadastrada. Crie a primeira no ícone "Mensagens prontas".'
                        : 'Nenhuma mensagem pronta cadastrada. Quem administra cria em Configurações → Mensagens prontas.'
                      : `Nenhuma mensagem pronta com "${comando.termo}".`,
              aoMover: (delta) =>
                definirDestaque((atual) => proximoDestaque(atual, delta, sugeridas.length)),
              aoEscolher: escolherSugerida,
              aoFechar: () => definirComandoFechado(true),
            }
      }
    >
      {avisoDaMencao && (
        <p className="editor__aviso-de-mencao" role="status">
          {avisoDaMencao}
        </p>
      )}
      {/* O seletor de arquivo fica fora da ordem de tabulação: a tabulação
          chega ao ícone da casca, e o ícone abre o seletor. */}
      {podeAnexar && (
        <input
          ref={seletor}
          type="file"
          className="rascunho__seletor"
          multiple
          accept={tiposParaOSeletor(capacidades.limitesDeAnexo)}
          onChange={(e) => {
            acrescentar([...(e.target.files ?? [])]);
            e.target.value = '';
          }}
          tabIndex={-1}
          aria-hidden="true"
        />
      )}

      {/* O motivo de não poder enviar, quando a janela não o disse. Junto do
          texto, e não no rodapé: com dois envios em curso não haveria como
          saber de qual deles era o erro. */}
      {permissao.porque && !travadoAteEscolherModelo && janelaDaCasca.estado !== 'fechada' && (
        <p className="rascunho__motivo" role="status">
          {permissao.porque}
        </p>
      )}
      {permissao.ressalva && (
        <p className="rascunho__ressalva" role="status">
          {permissao.ressalva}
        </p>
      )}

      {/* Escolhido, o modelo fica VISÍVEL e removível: some da vista assim que
          o diálogo fecha, e sem esta linha a pessoa volta ao campo sem saber
          qual dos quarenta escolheu. */}
      {!privada && template !== '' && (
        <div className="rascunho__modelo-escolhido">
          <p className="rascunho__modelo-linha">
            Mensagem pronta: <strong>{template}</strong>
            <button type="button" onClick={() => definirModelosAbertos(true)}>
              trocar
            </button>
            <button
              type="button"
              onClick={() => {
                definirTemplate('');
                definirValores([]);
              }}
            >
              tirar
            </button>
          </p>

          {/*
            O TEXTO que o cliente vai receber — e não o que se digita no campo.
            Fora da janela quem monta a mensagem é a Meta, a partir do modelo
            que ela aprovou. Sem isto, escolhia-se um nome e enviava-se sem ter
            lido a frase uma única vez.
          */}
          {/*
            Sem corpo, NADA — e não uma caixa vazia.

            O catálogo é gravado pelo worker e lido aqui. Uma subida que
            atualize um e não o outro deixa os dois em formatos diferentes, e
            nesse intervalo `corpo` chega vazio. Mordeu em 13/09/2026: a tela
            mostrou um retângulo com o trilho roxo e nada dentro, que parecia
            defeito de renderização e era imagem velha do worker.
          */}
          {escolhido && escolhido.corpo !== '' && (
            <p className="rascunho__modelo-corpo">{preenchido || escolhido.corpo}</p>
          )}

          {/*
            Um campo por variável. A Meta recusa envio com contagem diferente
            da aprovada, e o erro dela fala em NÚMERO — nunca em qual faltou.
          */}
          {escolhido && escolhido.variaveis > 0 && (
            <div className="rascunho__modelo-valores">
              {Array.from({ length: escolhido.variaveis }).map((_, i) => (
                <label key={i} className="rascunho__modelo-valor">
                  <span>{`{{${i + 1}}}`}</span>
                  <input
                    type="text"
                    value={valores[i] ?? ''}
                    maxLength={1000}
                    onChange={(e) => {
                      const novos = [...valores];
                      novos[i] = e.currentTarget.value;
                      definirValores(novos);
                    }}
                  />
                </label>
              ))}
            </div>
          )}
        </div>
      )}
      {modelosAbertos && (
        <DialogoDeModelos
          catalogo={capacidades.template.catalogo}
          prontas={mensagensProntas.data?.itens ?? []}
          escolhido={template}
          /*
           * Fora da janela, a aba da organização fica visível e TRAVADA.
           *
           * `permissao.exigeTemplate` é a resposta pronta de "o prazo fechou e
           * este canal só aceita modelo" — a mesma que trava o campo de texto.
           * Usar outro cálculo aqui abriria a chance de a caixa oferecer o que
           * o editor recusa.
           */
          foraDaJanela={!privada && permissao.exigeTemplate}
          situacaoDasProntas={situacaoDasProntas}
          aoTentarDeNovo={() => void mensagensProntas.refetch()}
          podeCriar={podeCriarPronta}
          rascunho={rascunhoDaPronta}
          aoMudarRascunho={definirRascunhoDaPronta}
          aoEscolher={definirTemplate}
          aoUsarPronta={inserirNoCampo}
          aoFechar={() => definirModelosAbertos(false)}
        />
      )}

      {contatoAberto && (
        <DialogoDeCompartilharContato
          aoUsar={inserirNoCampo}
          aoFechar={() => definirContatoAberto(false)}
        />
      )}

      {falhaDoMicrofone && (
        <p className="rascunho__motivo rascunho__motivo--recusa" role="alert">
          {falhaDoMicrofone}
        </p>
      )}
      {gravacao && (
        <div className="rascunho__gravando" role="status" aria-live="polite">
          <span className="rascunho__gravando-ponto" aria-hidden="true" />
          <span className="rascunho__gravando-texto">Gravando {duracaoEmPalavras(segundos)}</span>
          <button
            type="button"
            className="rascunho__gravando-descartar"
            onClick={descartarGravacao}
          >
            Descartar
          </button>
          <button
            type="button"
            className="rascunho__gravando-parar"
            onClick={() => void encerrarGravacao()}
          >
            Concluir
          </button>
        </div>
      )}
      {recusaDeAnexo && (
        <p className="rascunho__motivo rascunho__motivo--recusa" role="alert">
          {recusaDeAnexo.motivo}
        </p>
      )}

      {/* A prévia mostra o que vai sair, e não o nome do que vai sair. Mandar
          mídia é irreversível: aqui a pessoa vê a imagem, assiste ao vídeo e
          ouve o áudio antes de apertar enviar. Nome e tamanho só no que não é
          mídia — no documento, o nome é a ÚNICA forma de saber que o PDF é o
          certo; e ele continua no rótulo do botão de tirar. */}
      {anexos.length > 0 && (
        <ul className="rascunho__anexos" aria-label="Arquivos anexados">
          {anexos.map((anexo) => (
            <li
              key={anexo.id}
              className={[
                'rascunho__anexo',
                anexo.especie !== 'arquivo' ? 'rascunho__anexo--midia' : '',
                anexo.especie === 'audio' ? 'rascunho__anexo--audio' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <Previa anexo={anexo} />
              {anexo.especie === 'arquivo' && (
                <>
                  <span className="rascunho__anexo-nome" title={anexo.arquivo.name}>
                    {anexo.arquivo.name}
                  </span>
                  <span className="rascunho__anexo-tamanho">
                    {tamanhoEmPalavras(anexo.arquivo.size)}
                  </span>
                </>
              )}
              <button
                type="button"
                className="rascunho__anexo-tirar"
                onClick={() => remover(anexo.id)}
                aria-label={`Tirar ${anexo.arquivo.name}`}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* O contador só aparece perto do limite; a casca não sabe de limite. */}
      {!privada && medida && <Contador medida={medida} unidade={capacidades.unidadeDoLimite} />}
    </Editor>
  );
}

/**
 * A contagem regressiva da janela.
 *
 * *"Sabendo que restam duas horas, ele responde antes."* É a única parte desta
 * tela que existe para mudar o comportamento de quem a usa, e não só para
 * informar.
 */
/**
 * O que o anexo escolhido mostra de si antes de sair.
 *
 * Imagem aparece, vídeo toca, áudio toca — e o que não é mídia continua com o
 * ícone de clipe, porque não há o que pré-visualizar num PDF sem baixar um
 * leitor inteiro para dentro da tela.
 *
 * O `alt` da imagem é vazio e o ícone é `aria-hidden`: o nome do arquivo está
 * na linha ao lado, e um leitor de tela que anunciasse as duas coisas leria o
 * mesmo arquivo duas vezes. Os tocadores ficam acessíveis pelos controles
 * nativos, que já vêm rotulados pelo navegador.
 */
function Previa({ anexo }: { anexo: AnexoEscolhido }): ReactNode {
  if (anexo.previa === null) {
    return (
      <span className="rascunho__anexo-icone" aria-hidden="true">
        <IconeAnexo />
      </span>
    );
  }

  if (anexo.especie === 'imagem') {
    return <img className="rascunho__anexo-previa" src={anexo.previa} alt="" />;
  }

  if (anexo.especie === 'video') {
    return (
      <video
        className="rascunho__anexo-video"
        src={anexo.previa}
        controls
        preload="metadata"
        playsInline
      />
    );
  }

  return <audio className="rascunho__anexo-audio" src={anexo.previa} controls preload="metadata" />;
}

/**
 * O contador aparece PERTO do limite, não desde o primeiro caractere.
 *
 * Um número descendo a cada tecla desde "oi" é ruído, e ruído constante deixa
 * de ser lido. O que a Técnica pede é avisar antes de o trabalho se perder.
 */
function Contador({
  medida,
  unidade,
}: {
  medida: { tamanho: number; excesso: Excesso; limite: number | null };
  unidade: string;
}): ReactNode {
  const limite = medida.limite;
  if (limite === null || medida.tamanho <= limite * 0.8) return null;

  return (
    <span
      className={`rascunho__contador ${medida.excesso !== 'dentro' ? 'rascunho__contador--excedido' : ''}`}
    >
      {medida.tamanho.toLocaleString('pt-BR')} / {limite.toLocaleString('pt-BR')} {unidade}
      {medida.excesso === 'passou-do-recomendado' && ' — acima do recomendado para este canal'}
      {medida.excesso === 'passou-do-limite' && ' — acima do que o canal aceita'}
    </span>
  );
}
